import { QueryClient, QueryCache, MutationCache } from '@tanstack/react-query'
import { getErrorMessage, isUnauthorizedError } from './api-client'

// ============================================================================
// Error Handlers
// ============================================================================

const handleQueryError = (error: unknown) => {
  const message = getErrorMessage(error)

  // Don't log auth errors (handled by interceptor)
  if (!isUnauthorizedError(error)) {
    console.error('[Query Error]:', message)
  }

  // You can dispatch to a global toast/notification system here
  if (typeof window !== 'undefined') {
    window.dispatchEvent(
      new CustomEvent('api-error', {
        detail: { message, error },
      })
    )
  }
}

const handleMutationError = (error: unknown) => {
  const message = getErrorMessage(error)

  if (!isUnauthorizedError(error)) {
    console.error('[Mutation Error]:', message)
  }

  if (typeof window !== 'undefined') {
    window.dispatchEvent(
      new CustomEvent('api-error', {
        detail: { message, error },
      })
    )
  }
}

// ============================================================================
// Query Client Configuration
// ============================================================================

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Data freshness
      staleTime: 1000 * 60 * 5, // 5 minutes - data is fresh
      gcTime: 1000 * 60 * 30, // 30 minutes - garbage collection (cache retention)

      // Retry logic
      retry: (failureCount, error) => {
        // Don't retry on 4xx errors (client errors)
        if (error && typeof error === 'object' && 'status' in error) {
          const status = (error as { status: number }).status
          if (status >= 400 && status < 500) {
            return false
          }
        }
        // Retry up to 3 times for server errors
        return failureCount < 3
      },
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),

      // Refetching behavior
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
      refetchOnMount: true,

      // Network mode
      networkMode: 'online',
    },
    mutations: {
      retry: false,
      networkMode: 'online',
    },
  },
  queryCache: new QueryCache({
    onError: handleQueryError,
  }),
  mutationCache: new MutationCache({
    onError: handleMutationError,
  }),
})

// ============================================================================
// Query Keys Factory
// ============================================================================

export const queryKeys = {
  // Teams
  teams: {
    all: ['teams'] as const,
    lists: () => [...queryKeys.teams.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) => [...queryKeys.teams.lists(), filters] as const,
    active: () => [...queryKeys.teams.all, 'active'] as const,
    details: () => [...queryKeys.teams.all, 'detail'] as const,
    detail: (id: number | string) => [...queryKeys.teams.details(), id] as const,
    players: (id: number | string) => [...queryKeys.teams.detail(id), 'players'] as const,
  },

  // Players
  players: {
    all: ['players'] as const,
    lists: () => [...queryKeys.players.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) => [...queryKeys.players.lists(), filters] as const,
    active: () => [...queryKeys.players.all, 'active'] as const,
    byTeam: (teamId: number | string) => [...queryKeys.players.all, 'team', teamId] as const,
    details: () => [...queryKeys.players.all, 'detail'] as const,
    detail: (id: number | string) => [...queryKeys.players.details(), id] as const,
    stats: (id: number | string) => [...queryKeys.players.detail(id), 'stats'] as const,
  },

  // News
  news: {
    all: ['news'] as const,
    lists: () => [...queryKeys.news.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) => [...queryKeys.news.lists(), filters] as const,
    breaking: () => [...queryKeys.news.all, 'breaking'] as const,
    featured: () => [...queryKeys.news.all, 'featured'] as const,
    latest: (limit?: number) => [...queryKeys.news.all, 'latest', limit] as const,
    byTeam: (teamId: number | string) => [...queryKeys.news.all, 'team', teamId] as const,
    byPlayer: (playerId: number | string) => [...queryKeys.news.all, 'player', playerId] as const,
    details: () => [...queryKeys.news.all, 'detail'] as const,
    detail: (idOrSlug: number | string) => [...queryKeys.news.details(), idOrSlug] as const,
    related: (idOrSlug: number | string) => [...queryKeys.news.detail(idOrSlug), 'related'] as const,
    categories: () => [...queryKeys.news.all, 'categories'] as const,
  },

  // Matches
  matches: {
    all: ['matches'] as const,
    lists: () => [...queryKeys.matches.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) => [...queryKeys.matches.lists(), filters] as const,
    live: () => [...queryKeys.matches.all, 'live'] as const,
    upcoming: (limit?: number) => [...queryKeys.matches.all, 'upcoming', limit] as const,
    recent: (limit?: number) => [...queryKeys.matches.all, 'recent', limit] as const,
    details: () => [...queryKeys.matches.all, 'detail'] as const,
    detail: (id: number | string) => [...queryKeys.matches.details(), id] as const,
  },

  // Points Table
  points: {
    all: ['points'] as const,
    lists: () => [...queryKeys.points.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) => [...queryKeys.points.lists(), filters] as const,
    active: () => [...queryKeys.points.all, 'active'] as const,
    byTeam: (teamId: number | string) => [...queryKeys.points.all, 'team', teamId] as const,
    detail: (id: number | string) => [...queryKeys.points.all, 'detail', id] as const,
    stats: () => [...queryKeys.points.all, 'stats'] as const,
    topBatsmen: () => [...queryKeys.points.stats(), 'batsmen'] as const,
    topBowlers: () => [...queryKeys.points.stats(), 'bowlers'] as const,
  },

  // YouTube Videos
  videos: {
    all: ['youtube-videos'] as const,
    lists: () => [...queryKeys.videos.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) => [...queryKeys.videos.lists(), filters] as const,
    latest: () => [...queryKeys.videos.all, 'latest'] as const,
    featured: () => [...queryKeys.videos.all, 'featured'] as const,
    byTeam: (teamId: number | string) => [...queryKeys.videos.all, 'team', teamId] as const,
    byPlayer: (playerId: number | string) => [...queryKeys.videos.all, 'player', playerId] as const,
    detail: (id: number | string) => [...queryKeys.videos.all, 'detail', id] as const,
  },

  // Photo Galleries
  galleries: {
    all: ['photo-galleries'] as const,
    lists: () => [...queryKeys.galleries.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) => [...queryKeys.galleries.lists(), filters] as const,
    byTeam: (teamId: number | string) => [...queryKeys.galleries.all, 'team', teamId] as const,
    byPlayer: (playerId: number | string) => [...queryKeys.galleries.all, 'player', playerId] as const,
    detail: (id: number | string) => [...queryKeys.galleries.all, 'detail', id] as const,
  },

  // Legacy media (for backward compat)
  media: {
    all: ['media'] as const,
    videos: () => [...queryKeys.media.all, 'videos'] as const,
    videoList: (filters?: Record<string, unknown>) => [...queryKeys.media.videos(), filters] as const,
    galleries: () => [...queryKeys.media.all, 'galleries'] as const,
    galleryList: (filters?: Record<string, unknown>) => [...queryKeys.media.galleries(), filters] as const,
    gallery: (id: number | string) => [...queryKeys.media.galleries(), id] as const,
  },

  // Sliders
  sliders: {
    all: ['sliders'] as const,
    active: () => [...queryKeys.sliders.all, 'active'] as const,
  },

  // Schedules
  schedules: {
    all: ['schedules'] as const,
    lists: () => [...queryKeys.schedules.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) => [...queryKeys.schedules.lists(), filters] as const,
    details: () => [...queryKeys.schedules.all, 'detail'] as const,
    detail: (id: number | string) => [...queryKeys.schedules.details(), id] as const,
  },

  // Highlights
  highlights: {
    all: ['highlights'] as const,
    lists: () => [...queryKeys.highlights.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) => [...queryKeys.highlights.lists(), filters] as const,
    details: () => [...queryKeys.highlights.all, 'detail'] as const,
    detail: (id: number | string) => [...queryKeys.highlights.details(), id] as const,
  },

  // Venues
  venues: {
    all: ['venues'] as const,
    lists: () => [...queryKeys.venues.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) => [...queryKeys.venues.lists(), filters] as const,
    details: () => [...queryKeys.venues.all, 'detail'] as const,
    detail: (id: number | string) => [...queryKeys.venues.details(), id] as const,
  },
} as const

// ============================================================================
// Prefetch Helpers
// ============================================================================

export function prefetchQuery<T>(
  key: readonly unknown[],
  fetcher: () => Promise<T>
): Promise<void> {
  return queryClient.prefetchQuery({
    queryKey: key,
    queryFn: fetcher,
  })
}

export function invalidateQueries(key: readonly unknown[]): Promise<void> {
  return queryClient.invalidateQueries({ queryKey: key })
}

export function setQueryData<T>(key: readonly unknown[], data: T): void {
  queryClient.setQueryData(key, data)
}

export function getQueryData<T>(key: readonly unknown[]): T | undefined {
  return queryClient.getQueryData<T>(key)
}
